// Global
import React from 'react';

// Local
import { ProductResponse } from 'lib/services/models/content-response';
import useDictionary from 'lib/hooks/useDictionary';
import { ReplacementToken, tokenReplace } from 'lib/utils/string-utils';
import { SvgIcon } from 'components/helpers/SvgIcon';

import styles from './K12RegulatoryDocs.module.scss';

export interface K12RegulatoryDocsProps {
  product?: ProductResponse;
}

export function K12RegulatoryDocs({ product }: K12RegulatoryDocsProps) {
  const { getDictionaryValue } = useDictionary();
  const resolveDictionaryValue = (key: string, fallback: string, tokens?: ReplacementToken[]) => {
    const resolvedValue = getDictionaryValue(key, fallback, tokens);

    if (!resolvedValue || resolvedValue === key) {
      return tokens ? tokenReplace(fallback, tokens) : fallback;
    }

    return resolvedValue;
  };

  const productNumber = product?.ProductId || '';

  const creditingLetterAsset = product?.Assets?.find(
    (asset) => asset.ImageFacing === 'k12creditingletter'
  );
  const buyAmericanAsset = product?.Assets?.find((asset) => asset.ImageFacing === 'buyamerican');

  if (!creditingLetterAsset && !buyAmericanAsset) {
    return null;
  }

  const headline = resolveDictionaryValue(
    'Components.Product.K12RegulatoryDocs.Headline',
    'K-12 regulatory documentation'
  );

  const creditingLetterLabel = resolveDictionaryValue(
    'Components.Product.K12RegulatoryDocs.CreditingLetterLabel',
    'Crediting Letter - {ProductNumber} (pdf)',
    [{ key: '{ProductNumber}', value: productNumber }]
  );

  const buyAmericanLabel = resolveDictionaryValue(
    'Components.Product.K12RegulatoryDocs.BuyAmericanLabel',
    'Buy American Letter - {ProductNumber} (pdf)',
    [{ key: '{ProductNumber}', value: productNumber }]
  );

  return (
    <div className={styles.k12RegulatoryDocsContainer}>
      <div className={styles.k12RegulatoryDocsContent}>
        <h2 className={styles.k12RegulatoryDocsHeading}>{headline}</h2>
      </div>
      <ul className={styles.k12RegulatoryDocsLinkList}>
        {creditingLetterAsset && (
          <li className={styles.k12RegulatoryDocsItem}>
            <a
              href={creditingLetterAsset.Url}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.k12RegulatoryDocsLink}
            >
              <span>{creditingLetterLabel}</span>
              <SvgIcon icon="new-tab" size="sm" iconPosition="after" aria-hidden="true" />
            </a>
          </li>
        )}
        {buyAmericanAsset && (
          <li className={styles.k12RegulatoryDocsItem}>
            <a
              href={buyAmericanAsset.Url}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.k12RegulatoryDocsLink}
            >
              <span>{buyAmericanLabel}</span>
              <SvgIcon icon="new-tab" size="sm" iconPosition="after" aria-hidden="true" />
            </a>
          </li>
        )}
      </ul>
    </div>
  );
}
